import { Box } from "@chakra-ui/react";
import React from "react";
import {
  CalendarIcon,
  CornerDownRightIcon,
  ErrorIcon,
  GiftIcon,
  GlobeIcon,
  HeartIcon,
  LightningIcon,
  MapIcon,
  MinusCircleIcon,
  MobilePhoneIcon,
  MoneyIcon,
  PercentIcon,
  PersonWithCheckIcon,
  PinIcon,
  RefreshIcon,
  SearchIcon,
  SettingsIcon,
  SparklesIcon,
  SunshineIcon,
  TargetIcon,
  TrendIcon,
} from "ui/icons";
import hero from "../../../../public/assets/cdp-industries/heros/travel-hospitality.png";
import { dataBreachStat, personalizedExperiencesStat, revenueReferenceCopy, type CdpIndustryConfig } from "./shared";

export const travelHospitalityCdpConfig: CdpIndustryConfig = {
  icon: MapIcon,
  name: "travel & hospitality",
  titleCaseName: "Travel & Hospitality",
  pluralNoun: "travel & hospitality companies",
  color: "blue",
  seoCopy:
    "Leverage all your travel & hospitality data, including customers, bookings, properties, loyalty members, room preferences, and dining reservations.",
  heroImage: {
    src: hero,
    alt: "A photograph of a tropical resort pool with a list of example data tables overlayed. The data tables included are for room preferences, dining reservations, bookings, loyalty members, and properties.",
  },
  adoptingCdpsDescription: (
    <>
      Travel and hospitality is an incredibly competitive industry where customers can easily compare prices, reviews,
      and experiences. {revenueReferenceCopy} If you don’t give travelers a compelling reason to book with you, they’ll
      simply go to one of your competitors.
    </>
  ),
  featuredOutcomes: [
    {
      icon: HeartIcon,
      color: "red",
      title: "Build loyalty",
    },
    {
      icon: TrendIcon,
      color: "green",
      title: "Grow bookings",
    },
    {
      icon: RefreshIcon,
      color: "yellow",
      title: "Reduce churn",
    },
  ],
  dataStatsTitleMaxW: "3xl",
  dataStats: [
    {
      title: "$1.1T",
      description: (
        <>
          estimated market size for
          <Box as="br" display={{ base: "block", lg: "none" }} /> travel & tourism by 2029
        </>
      ),
      href: "https://www.statista.com/outlook/mmo/travel-tourism/worldwide",
    },
    personalizedExperiencesStat,
    dataBreachStat,
  ],
  personalizationCards: [
    {
      title:
        "United Airlines is introducing the first traveler media network to display targeted in-flight ads to users",
      href: "https://www.marketingdive.com/news/united-airlines-retail-media-network-kinective-industry-first/718427/",
    },
    {
      title:
        "Hilton uses AI to deliver relevant and personalized experiences to individual customers based on their preferences",
      href: "https://www.costar.com/article/1944095139/personalized-content-experiences-fuel-hiltons-data-modernization",
    },
    {
      title:
        "Accor is leveraging Hightouch & Snowflake to tailor guest experiences specifically to their interests at check-in",
      href: "https://www.snowflake.com/en/customers/all-customers/case-study/accor/",
    },
  ],
  useCases: {
    performanceMarketing: [
      // First row
      {
        icon: PersonWithCheckIcon,
        title: "Acquisition campaigns",
        description:
          "Drive new bookings by using demographic and travel intent data to target potential travelers and lookalike audiences with exclusive offers, increasing occupancy and reservations.",
      },
      {
        icon: MinusCircleIcon,
        title: "Suppression campaigns",
        description:
          "Reduce wasted ad spend by suppressing loyalty members or frequent travelers from acquisition campaigns using CRM data, reallocating budget toward new or lapsed customers.",
      },
      {
        icon: TargetIcon,
        title: "Retargeting campaigns",
        description:
          "Re-engage users who browsed travel packages but didn’t book by serving dynamic ads featuring their explored destinations and limited-time discounts, recovering lost bookings.",
      },
      // Second row
      {
        icon: MoneyIcon,
        title: "Cross-sell campaigns",
        description:
          "Promote ancillary travel services by using booking data to target customers with ads for complementary products like travel insurance or airport transfers, increasing ancillary revenue.",
      },
      {
        icon: SettingsIcon,
        title: "Conversion APIs",
        description:
          "Share online and offline conversion data with ad platforms, enabling algorithms to better identify and target users with a higher likelihood of converting to improve campaign performance.",
      },
      {
        icon: GlobeIcon,
        title: "Media networks",
        description:
          "Monetize customer insights by offering anonymized audience segments to brand partners for targeted advertising, generating incremental revenue through premium audience data sales.",
      },
    ],
    lifecycleMarketing: [
      // First row
      {
        icon: MoneyIcon,
        title: "Upsell & cross-sell campaigns",
        description:
          "Recommend room upgrades, premium cabin classes, or tour packages during and after booking to increase revenue per customer.",
      },
      {
        icon: SunshineIcon,
        title: "Seasonal campaigns",
        description:
          "Promote seasonal travel offers, such as ski resorts in winter or beach vacations in summer, driving urgency and peak-season bookings.",
      },
      {
        icon: ErrorIcon,
        title: "Churn prevention campaigns",
        description:
          "Identify and re-engage frequent travelers who haven’t booked in a while by offering personalized discounts or loyalty rewards.",
      },
      // Second row
      {
        icon: PinIcon,
        title: "Localized promotions",
        description:
          "Promote staycations, local events, or last-minute deals to increase bookings from nearby travelers.",
      },
      {
        icon: CalendarIcon,
        title: "Anniversary campaigns",
        description:
          "Celebrate customer loyalty milestones with exclusive offers, such as extra loyalty points or discounts on repeat bookings.",
      },
      {
        icon: RefreshIcon,
        title: "Reactivation campaigns",
        description: "Re-engage inactive travelers with exclusive offers or discounts to encourage repeat bookings.",
      },
      // Third row
      {
        icon: SparklesIcon,
        title: "Membership engagement campaigns",
        description:
          "Encourage loyalty program participation with exclusive perks like double points, free upgrades, or early access to deals.",
      },
      {
        icon: GiftIcon,
        title: "Special occasion campaigns",
        description:
          "Offer personalized promotions for birthdays, anniversaries, or milestone events to increase emotional engagement.",
      },
      {
        icon: MapIcon,
        title: "Post-trip campaigns",
        description:
          "Follow up with travelers after their trip to encourage reviews, recommendations, and future bookings.",
      },
    ],
    onsitePersonalization: [
      // First row
      {
        icon: LightningIcon,
        title: "Dynamic home page content",
        description:
          "Display personalized banners featuring recommended destinations, hotel deals, or flight offers based on browsing history to increase engagement and boost conversions.",
      },
      {
        icon: MobilePhoneIcon,
        title: "Dynamic app experiences",
        description:
          "Simplify trip management by highlighting upcoming trip details, one-click check-ins, or weather updates for destinations on the app’s home screen, improving user retention and experience.",
      },
      {
        icon: SearchIcon,
        title: "Dynamic search results",
        description:
          "Tailor search filters to prioritize trending destinations, personalized travel deals, and user preferences to accelerate the booking process and improve conversion rates.",
      },
      // Second row
      {
        icon: MapIcon,
        title: "Multi-destination recommendations",
        description:
          "Suggest additional destinations or stopovers based on current bookings to increase trip value and drive incremental revenue.",
      },
      {
        icon: CornerDownRightIcon,
        title: "Next best action",
        description:
          "Encourage high-value travel actions such as joining a loyalty program, booking add-ons, or upgrading flights/accommodations to increase engagement and revenue.",
      },
      {
        icon: PinIcon,
        title: "Live event recommendations",
        description:
          "Use geolocation to suggest local concerts, tours, or dining experiences in the traveler’s destination, driving ancillary revenue and improving customer satisfaction.",
      },
      // Third row
      {
        icon: TrendIcon,
        title: "Travel goal tracking",
        description:
          "Show progress toward travel rewards like free trips or exclusive upgrades for frequent bookers, deepening engagement and incentivizing future bookings.",
      },
      {
        icon: PercentIcon,
        title: "Early bird campaigns",
        description:
          "Promote early-bird discounts for customers planning vacations months in advance to stabilize bookings and lock in revenue earlier.",
      },
      {
        icon: SparklesIcon,
        title: "Interactive itineraries",
        description:
          "Provide real-time updates on trip details, including weather forecasts, local events, and dining suggestions, to enhance the traveler’s experience and increase app engagement.",
      },
    ],
  },
  considerations: {
    dataModeling: {
      traditional: "Uses predefined models that may not fully capture booking trends or traveler itineraries",
      composable: "Supports tailored models to handle booking histories, travel behaviors, and preferences",
    },
    audienceManagement: {
      traditional:
        "Supports broad segmentation but struggles with categories like frequent travelers or package preferences",
      composable: "Enables granular segmentation using traveler profiles, booking trends, and loyalty program tiers",
    },
    customerJourneys: {
      traditional: "Provides standard templates for campaigns like email confirmations or standard promotions",
      composable:
        "Powers fully adaptable journeys for events like multi-leg trips, travel upgrades, and loyalty rewards",
    },
    identityResolution: {
      traditional:
        "Relies on out-of-the-box algorithms that may not unify profiles across booking systems loyalty programs and travel apps",
      composable:
        "Supports custom algorithms to unify profiles across booking platforms, airline partners, and loyalty ecosystems",
    },
  },
  whyHightouchTitleMaxW: "750px",
  audienceOverlap: {
    title: "Vacation planners with unbooked trips",
    firstCategoryLabel: "Flight search abandoners",
    secondCategoryLabel: "Loyalty program members",
  },
};
